import {
  formatCurrency,
  getLegacyCheckRouteFromDealsResponse,
  getLegacyCheckStatusValueFromDealsResponse,
  isInstantReleaseCheckPendingFromDealsResponse,
  isSVDApplicationApproved,
  isSVDCardRequested,
  isSVDDebitCardActivated,
  isSVDDenied,
  isSVDDepositAccountCreated,
  isSVDPending,
  isSVDPendingVerification,
  isSVDReview,
  isSVDVerificationPendingStatus,
  isSVDVerificationRejected,
  isSVDVerificationRequired,
  shouldShowInstantReleasePage,
  shouldShowInstantReleaseResumePage,
  shouldShowLegacyOnlyInstantReleasePage,
} from "../../Utils/helpers";

// The Synctera webhook creates the account and moves the status to DepositAccountCreated. Anything
// before that (ApplicationApproved, VerificationPending, under review...) is still in progress.
export const hasDepositAccount = (stakeDebit) =>
  isSVDDepositAccountCreated(stakeDebit) || isSVDCardRequested(stakeDebit) || isSVDDebitCardActivated(stakeDebit);

/** Which instant-release screen matches the Stake Checking status (`stakeDebit` is the response's `data`). */
export function screenForApplicationOutcome(stakeDebit) {
  if (isSVDDenied(stakeDebit) || isSVDVerificationRejected(stakeDebit)) return "denied";
  if (isSVDPendingVerification(stakeDebit) || isSVDVerificationRequired(stakeDebit)) return "details";
  if (isSVDReview(stakeDebit)) return "review";
  if (isSVDPending(stakeDebit) || isSVDVerificationPendingStatus(stakeDebit)) return "pending";
  if (hasDepositAccount(stakeDebit)) return "approved";
  if (isSVDApplicationApproved(stakeDebit)) return "pending";
  return "pending";
}

function text(value) {
  if (value == null) return "";
  return String(value).trim();
}

export function readRewardAmount(partnerCustomer) {
  const amount = Number(partnerCustomer?.incentive_amount);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
}

export function formatReward(amount, { cents = false, trailingPeriod = false } = {}) {
  if (amount == null || Number.isNaN(Number(amount))) return "";
  const formatted = formatCurrency(amount, cents ? 2 : 0);
  if (!formatted) return "";
  return trailingPeriod ? `${formatted}.` : formatted;
}

export function isPoBox(value) {
  return /\bP\.?\s*O\.?\s*Box\b/i.test(String(value || ""));
}

export function buildInitialForm(partnerCustomer, onboardedCustomer) {
  const street = text(partnerCustomer?.address);
  const apt = text(partnerCustomer?.unit);
  const city = text(partnerCustomer?.city);
  const zip = text(partnerCustomer?.zipCode).replace(/\D/g, "").slice(0, 5);

  return {
    firstName: text(partnerCustomer?.first_name),
    lastName: text(partnerCustomer?.last_name),
    email: text(onboardedCustomer?.email) || text(partnerCustomer?.email),
    mailStreet: street,
    mailApt: apt,
    mailCity: city,
    mailState: "",
    mailZip: zip,
  };
}

const STATUS_SCREENS = ["pending", "review", "details", "denied", "approved", "card", "legacy", "confirm", "waiting", "mailing"];

export function screenFromStatus(value) {
  const key = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return STATUS_SCREENS.includes(key) ? key : null;
}

export function screenFromBattlecardState(dealsResponse, unclaimedTransactionsResponse, stakeDebitResponse) {
  if (shouldShowInstantReleasePage(dealsResponse, unclaimedTransactionsResponse, stakeDebitResponse)) {
    return "landing";
  }

  // Application submitted but not finished (details needed, pending, under review): resume on the matching screen.
  if (shouldShowInstantReleaseResumePage(dealsResponse, unclaimedTransactionsResponse, stakeDebitResponse)) {
    return screenForApplicationOutcome(stakeDebitResponse?.data);
  }

  if (shouldShowLegacyOnlyInstantReleasePage(dealsResponse, unclaimedTransactionsResponse, stakeDebitResponse)) {
    return "denied";
  }

  if (!isInstantReleaseCheckPendingFromDealsResponse(dealsResponse)) return null;

  const route = getLegacyCheckRouteFromDealsResponse(dealsResponse);
  if (route === "/battlecard/check-not-processed") return "notProcessed";

  const status = getLegacyCheckStatusValueFromDealsResponse(dealsResponse);
  if (status === "check_mailed") return "mailing";
  return "waiting";
}

const INSTANT_RELEASE_PENDING_KEY = "instant_release_pending";

export function syncInstantReleaseFromSearch(params) {
  sessionStorage.setItem(
    INSTANT_RELEASE_PENDING_KEY,
    JSON.stringify({
      partnerId: params.get("partnerId") || "",
      customerReferenceId: params.get("customerReferenceId") || "",
      status: params.get("status") || "",
    })
  );
}

export function readInstantReleasePending() {
  try {
    const raw = sessionStorage.getItem(INSTANT_RELEASE_PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.partnerId == null) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function clearInstantReleasePending() {
  sessionStorage.removeItem(INSTANT_RELEASE_PENDING_KEY);
}
